import Logger from '../lib/logger';
import { handleize } from '../lib/util.functions';

class ThemeCompareModels extends HTMLElement {
  #NAME = 'ThemeCompareModels'
  #abortController
  #form
  #compareGridForm
  #compareContainer
  #searchParams
  #data = []
  #timer
  #tipTimer

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    this.#form = this.querySelector('form#models-form');
    this.#compareContainer = this.querySelector('#compare-container');
    this.#compareGridForm = this.querySelector('form#compare-grid-form');
    this.#searchParams = new URLSearchParams(window.location.search);
  }

  connectedCallback() {
    Logger.info(`${this.#NAME} > connectedCallback`);
    //
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    //
    this.#form.addEventListener('change', (event) => this.#onFormChange(this.#form, event), { signal });
    this.#form.addEventListener('click', (event) => this.#onCardClick(event), { signal });
    this.#compareGridForm.addEventListener('change', (event) => this.#onFormChange(this.#compareGridForm, event), { signal });
    this.querySelectorAll('.theme-compare-models__container__grid__image-wrap__hover').forEach(el => {
      el.addEventListener('mouseover', () => {
        if (this.#timer) {
          clearTimeout(this.#timer);
          this.#timer = null;
        }
      }, { signal });
    })
    window.addEventListener('resize', () => this.#updateState(true), { signal });
    window.addEventListener('hashchange', () => this.#selectAllFromHash(), { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = null;
    if (this.#tipTimer) {
      clearTimeout(this.#tipTimer);
      this.#tipTimer = null;
    }
  }

  static get observedAttributes() {
    return ['data-json'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    Logger.info(`${this.#NAME} > attributeChangedCallback > name: ${name}`);
    if (name === 'data-json') {
      this.#data = JSON.parse(newValue);
      Logger.info(`${this.#NAME} > #data >`, this.#data);
      this.#updateState(true);
      this.#selectAllFromHash();
    }
  }

  #onFormChange(form, event) {
    Logger.info(`${this.#NAME} > #onFormChange`);
    const keys = Array.from(new FormData(form).keys());
    this.#searchParams.set('checked', keys.join(','));
    this.#updateState();
    if (form === this.#form) {
      this.#maybeShowCompareTip(event);
    }
    if (!keys.length && form === this.#compareGridForm) {
      this.#form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (keys.length && form === this.#form) {
      if (this.#timer) {
        clearTimeout(this.#timer);
        this.#timer = null;
      }
      this.#timer = setTimeout(() => {
        this.#compareContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 2500);
    }
  }

  #updateState(replace = false) {
    const state = {};
    for (const [key, value] of this.#searchParams.entries()) {
      state[key] = value
    }
    const queryString = this.#searchParams.toString();
    const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;
    //
    if (replace) {
      history.replaceState(state, '', nextUrl);
    } else {
      history.pushState(state, '', nextUrl);
    }
    //
    this.#onStateChange(state);
  }

  #onStateChange(state) {
    Logger.info(`${this.#NAME} > #onStateChange`, state);
    //
    const curIds = state.checked?.split(',').filter(id => id != '') || [];
    let gridStyle = '';
    [ 
      ...Array.from(this.#form.querySelectorAll('input')), 
      ...Array.from(this.#compareGridForm.querySelectorAll('input'))
    ].forEach(input => {
      input.checked = curIds.includes(input.name);
    });
    const w = window.innerWidth;
    const filteredData = this.#data.filter(obj => curIds.includes(obj.id));
    this.#compareContainer.dataset.show = filteredData.length ? 'true' : 'false';
    const row1 = `${w < 720 ? '' :'blank-top-legend '}${filteredData.map(obj => `head-${obj.id}`).join(' ')}`;
    const rowLast = `${w < 720 ? '' : 'blank-bottom-legend '}${filteredData.map(obj => `label-${obj.id}`).join(' ')}`;
    const featuresLength = filteredData[0]?.features.length || 0;
    gridStyle = gridStyle.concat(`"${row1}"`);
    for (let index = 0; index < featuresLength; index++) {
      const handle = handleize(filteredData[0].features[index].feature);
      const row = `${w < 720 ? '' : `${handle}-legend `}${filteredData.map(obj => `${handle}-${obj.id}`).join(' ')}`;
      gridStyle = gridStyle.concat(` "${row}"`);
    }
    gridStyle = gridStyle.concat(` "${rowLast}"`);
    this.#compareGridForm.style.gridTemplateAreas = gridStyle;
    /* Logger.info(`${this.#NAME} > gridStyle`, gridStyle); */
    Array.from(this.#compareGridForm.children).forEach(el => {
      if (!el.dataset.legend) {
        el.dataset.show = curIds.includes(el.dataset.id);
      }
    })
  }

  #maybeShowCompareTip(event) {
    const input = event?.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.checked) {
      return;
    }

    try {
      if (window.localStorage.getItem(this.#getCompareTipKey())) {
        return;
      }
    } catch (error) {
      Logger.info(`${this.#NAME} > #maybeShowCompareTip > localStorage unavailable`, error);
    }

    const label = input.closest('label');
    if (!label) {
      return;
    }

    let tip = label.querySelector('[data-compare-tip]');
    if (!tip) {
      tip = document.createElement('span');
      tip.className = 'theme-compare-models__compare-tip';
      tip.dataset.compareTip = 'true';
      tip.textContent = 'Scroll down to compare models';
      label.appendChild(tip);
    }

    if (this.#tipTimer) {
      clearTimeout(this.#tipTimer);
      this.#tipTimer = null;
    }

    tip.dataset.show = 'true';

    try {
      window.localStorage.setItem(this.#getCompareTipKey(), 'true');
    } catch (error) {
      Logger.info(`${this.#NAME} > #maybeShowCompareTip > could not persist state`, error);
    }

    this.#tipTimer = setTimeout(() => {
      tip.dataset.show = 'false';
      this.#tipTimer = null;
    }, 2000);
  }

  #getCompareTipKey() {
    return `theme-compare-models-tip-seen:${window.location.pathname}`;
  }

  #onCardClick(event) {
    const link = event.target.closest('.theme-compare-models__container__grid__image-wrap[href]');
    if (!link || !this.#form.contains(link)) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    if (event.metaKey || event.ctrlKey) {
      window.open(link.href, '_blank', 'noopener');
      event.preventDefault();
      return;
    }

    if (event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    window.location.assign(link.href);
  }

  #selectAllFromHash() {
    if (!this.id || window.location.hash !== `#${this.id}`) return;
    if (!this.#data.length) return;
    const allIds = this.#data.map(obj => obj.id).join(',');
    this.#searchParams.set('checked', allIds);
    this.#updateState(true);
    requestAnimationFrame(() => {
      this.#compareContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    history.replaceState(history.state, '', window.location.pathname + window.location.search);
  }

}

customElements.define('theme-compare-models', ThemeCompareModels);
